import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthenticatedClient } from '@/api/queries'
import { useAuth } from '@/hooks/use-auth'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'

const LIST_KEY = ['agent', 'conversations'] as const
const DETAIL_KEY = (uuid: string) => ['agent', 'conversation', uuid] as const

export function useAgentConversations(includeArchived = false) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: [...LIST_KEY, includeArchived],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/agent/conversations', {
        params: { query: { include_archived: includeArchived } },
      })
      if (error) throw error
      return data!
    },
  })
}

export function useAgentConversation(uuid: string | null) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: DETAIL_KEY(uuid ?? ''),
    enabled: !!uuid,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/agent/conversations/{uuid}', {
        params: { path: { uuid: uuid! } },
      })
      if (error) throw error
      return data!
    },
  })
}

export function useCreateAgentConversation() {
  const getClient = useAuthenticatedClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (title?: string) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/agent/conversations', {
        body: { title: title ?? null },
      })
      if (error) throw error
      return data!
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY })
    },
  })
}

export function useUpdateAgentConversation() {
  const getClient = useAuthenticatedClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { uuid: string; title?: string; archived?: boolean }) => {
      const client = await getClient()
      const { data, error } = await client.PATCH('/dashboard/agent/conversations/{uuid}', {
        params: { path: { uuid: vars.uuid } },
        body: { title: vars.title ?? null, archived: vars.archived ?? null },
      })
      if (error) throw error
      return data!
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY })
    },
  })
}

export interface UploadedAgentAttachment {
  uuid: string
  filename: string
  content_type: string | null
  size_bytes: number
  extraction_status: string
  extracted_text_truncated: boolean
  extraction_error: string | null
  is_image: boolean
  is_pdf: boolean
}

export function useUploadAgentAttachment() {
  const { getAccessToken } = useAuth()
  return useMutation({
    mutationFn: async ({
      conversationUuid,
      file,
    }: {
      conversationUuid: string
      file: File
    }): Promise<UploadedAgentAttachment> => {
      const token = await getAccessToken()
      if (!token) throw new Error('Not authenticated')
      const fd = new FormData()
      fd.append('file', file)
      const resp = await fetch(
        `${API_BASE_URL}/dashboard/agent/conversations/${conversationUuid}/attachments`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        },
      )
      if (!resp.ok) {
        let detail = `${resp.status}`
        try {
          const body = (await resp.json()) as { detail?: string }
          if (body?.detail) detail = body.detail
        } catch {
          // ignore — fall back to status code
        }
        throw new Error(detail)
      }
      return (await resp.json()) as UploadedAgentAttachment
    },
  })
}

export function useDeleteAgentAttachment() {
  const { getAccessToken } = useAuth()
  return useMutation({
    mutationFn: async ({
      conversationUuid,
      attachmentUuid,
    }: {
      conversationUuid: string
      attachmentUuid: string
    }) => {
      const token = await getAccessToken()
      if (!token) throw new Error('Not authenticated')
      const resp = await fetch(
        `${API_BASE_URL}/dashboard/agent/conversations/${conversationUuid}/attachments/${attachmentUuid}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      if (!resp.ok && resp.status !== 404) {
        throw new Error(`Failed to delete attachment: ${resp.status}`)
      }
      return attachmentUuid
    },
  })
}

export function useDeleteAgentConversation() {
  const getClient = useAuthenticatedClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (uuid: string) => {
      const client = await getClient()
      const { error } = await client.DELETE('/dashboard/agent/conversations/{uuid}', {
        params: { path: { uuid } },
      })
      if (error) throw error
      return uuid
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY })
    },
  })
}
